import { useFileScope } from '../../../hooks/signalr/useFileScope'
import { useAuth } from '../../../hooks'
import React, { useEffect, useState, useRef, Fragment, useContext } from 'react'
import type { JSX } from 'react'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import { Add, ErrorOutline, SmsFailed } from '@mui/icons-material'
import { useMediaQuery } from 'react-responsive'
import { useFile } from '../../../hooks/api/documentHooks'
import {
	PREVIEW_LOADING_STATUSES,
	usePreviewWithPolling,
} from '../../../hooks/api/usePreviewWithPolling'
import {
	ConfirmationModal,
	InputModal,
} from '../../../components/modals/common'
import { DocumentData } from '../../../lib/api/types/document.interfaces'
import toastCaller, { errors } from '../../../lib/utils/toastCaller'
import Loader from '../../../components/common/Loader'
import PasswordProtectedFile from './components/PasswordProtectedFile'
import { ChatsItem } from '../../../lib/api/types/document.interfaces'
import { Input } from '../../../components/ui'
import PreviewHeader from './components/PreviewHeader'
import MobileAppPreview from './components/MobileAppPreview'
import Chat from './components/Chat'
import ChatsMenu from './components/Chat/ChatsMenu'
import { FileInfoModal } from '../../../components/modals/workspace'
import { useDeleteChat, useUpdateChat } from '../../../hooks/api/chatHooks'
import { ClassNameValue } from 'tailwind-merge'
import cn from '../../../lib/utils/cn'
import { useFileRendering } from '../../../hooks/file/useFileRendering'
import { useRenderScheduler } from '../../../hooks/file/useRenderScheduler'

type ExtendedChatsItem = ChatsItem & Partial<{ isNew: boolean }>

interface PreviewModalProps {
	isOpen: boolean
	onClose: () => void
	fileId: string | null
	versionId?: string
	initialFileInfo?: DocumentData | null
	position?: 'left' | 'right'
	classNameOverlay?: ClassNameValue
	classNameModal?: ClassNameValue
}

const PreviewModal: React.FC<PreviewModalProps> = ({
	isOpen,
	onClose,
	fileId,
	versionId,
	initialFileInfo,
	position = 'right',
	classNameOverlay,
	classNameModal,
}): JSX.Element => {
	// const { isApp } = useContext(AppContext)
	const { isAuthenticated } = useAuth()
	useFileScope(fileId, isOpen && isAuthenticated)
	const isMobile = useMediaQuery({ maxWidth: 800 })
	const isTablet = useMediaQuery({ maxWidth: 1024 })
	const shouldUseMobilePreview = React.useMemo(() => {
		if (typeof window === 'undefined' || typeof navigator === 'undefined') {
			return isMobile || isTablet
		}

		const hasCoarsePointer =
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(pointer: coarse)').matches
		const hasTouch = navigator.maxTouchPoints > 1

		return isMobile || (isTablet && (hasCoarsePointer || hasTouch))
	}, [isMobile, isTablet])

	const { data: fetchedFileData, isLoading: isLoadingFile } = useFile(
		{
			id: fileId ?? '',
		},
		{ enabled: isOpen && !!fileId && isAuthenticated },
	)

	const fileData =
		fetchedFileData ?? (initialFileInfo ? { data: initialFileInfo } : undefined)

	const currentDocument = fetchedFileData?.data || initialFileInfo

	const [isFileInfoOpen, setIsFileInfoOpen] = useState(false)
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const [isChatOpen, setIsChatOpen] = useState(false)
	const [selectedChat, setSelectedChat] = useState<ExtendedChatsItem | null>(
		null,
	)
	const [saveModalStatus, setSaveModalStatus] = useState<
		'close' | 'create' | null
	>(null)
	const [inputModalStatus, setInputModalStatus] = useState<
		'close' | 'create' | null
	>(null)
	const [isError, setIsError] = useState(false)
	useEffect(() => {
		setIsError(false)
	}, [fileId, fetchedFileData?.data?.version])

	const methods = useForm<{
		scale: number | 'fit'
		paginationPage: number
		currentPage: number
	}>({
		defaultValues: { scale: 'fit', paginationPage: 1, currentPage: 1 },
	})

	const { control, watch, setValue, reset } = methods
	const { scale, currentPage } = watch()

	const { mutateAsync: updateChat, isPending: isChanging } = useUpdateChat()
	const { mutateAsync: deleteChat, isPending: isDeleting } = useDeleteChat()

	const {
		status: previewStatus,
		data: previewData,
		error: previewError,
		isLoading: isRenderingToPDF,
	} = usePreviewWithPolling({
		id: fileId ?? '',
		versionId: versionId ?? undefined,
		enabled: Boolean(isOpen && fileId && isAuthenticated && !isError),
		file: fetchedFileData?.data,
	})

	const downloadUrl = React.useMemo(() => {
		if (!previewData?.data?.downloadUrl) return null
		return previewData.data.downloadUrl
	}, [previewData?.data?.downloadUrl])

	const {
		isRendering,
		renderError,
		isPasswordProtected,
		unlockPdf,
		numPages,
		fullPages,
		renderedPages,
		pdfDocument,
		renderPageBatch,
		reset: resetFileRendering,
	} = useFileRendering({
		downloadUrl,
		previewStatus,
		isRenderingToPDF,
		enabled: Boolean(isOpen && !isError && !shouldUseMobilePreview),
		isMobile,
	})

	const canvasRefs = useRef<HTMLDivElement[]>([])
	const isScrolling = useRef(false)
	const modalContentRef = useRef<HTMLDivElement>(null)
	const [contentWidth, setContentWidth] = useState(0)

	const { scheduleRender } = useRenderScheduler({ isMobile })

	const handleClose = () => {
		reset()
		resetFileRendering()
		setIsMenuOpen(false)
		setIsChatOpen(false)
		setSelectedChat(null)
		setIsError(false)
		canvasRefs.current = []
		onClose()
	}

	const isLoading = isRendering || isRenderingToPDF
	const hasNotReadyStatus =
		previewStatus === 'loading' ||
		(PREVIEW_LOADING_STATUSES as readonly string[]).includes(
			previewStatus as any,
		)
	const needsFirstPaint =
		previewStatus === 'ready' &&
		(numPages === null || renderedPages.length === 0)
	const showOverlayLoader =
		!renderError &&
		!shouldUseMobilePreview &&
		(hasNotReadyStatus || (!downloadUrl && isLoading) || needsFirstPaint)
	const overlayMessage = needsFirstPaint
		? 'Generating...'
		: errors[previewStatus as any]?.() ||
			(previewStatus === 'loading'
				? 'Loading preview...'
				: 'Generating preview, please wait...')

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		if (isScrolling?.current) return
		const container = event.currentTarget
		const scrollTop = container.scrollTop
		const containerRect = container.getBoundingClientRect()
		const viewportHeight = containerRect.height
		const visiblePages = canvasRefs.current
			.map((ref, index) => {
				if (!ref) return null
				const rect = ref.getBoundingClientRect()
				return {
					index,
					top: rect.top - containerRect.top + scrollTop,
					bottom: rect.bottom - containerRect.top + scrollTop,
				}
			})
			.filter((page): page is NonNullable<typeof page> => page !== null)
			.filter(
				(page) =>
					page.top < scrollTop + viewportHeight && page.bottom > scrollTop,
			)
		const pagesToRender = visiblePages
			.map((page) => page?.index + 1)
			.filter((pageNum) => !renderedPages.includes(pageNum))
		if (pagesToRender.length > 0 && pdfDocument) {
			scheduleRender(() => {
				if (isMobile) {
					const focusedPage = Math.min(...pagesToRender)
					const startPage = Math.max(1, focusedPage - 1)
					const endPage = Math.min(numPages || 0, focusedPage + 1)
					renderPageBatch(pdfDocument, startPage - 1, endPage)
				} else {
					const startIndex = Math.min(...pagesToRender) - 1
					const endIndex = Math.min(startIndex + 20, numPages || 0)
					renderPageBatch(pdfDocument, startIndex, endIndex)
				}
			})
		}
		const pageRects = canvasRefs.current.map((ref) =>
			ref ? ref.getBoundingClientRect().top - containerRect.top + scrollTop : 0,
		)
		// First page whose next boundary is after scrollTop (handles padding when scrollTop === 0)
		let calculatedPage = pageRects.findIndex(
			(_, index) =>
				index === pageRects.length - 1 || scrollTop < pageRects[index + 1],
		)
		if (calculatedPage === -1) {
			calculatedPage = 0
		}
		const newPage = calculatedPage + 1

		if (newPage !== currentPage) {
			setValue('paginationPage', newPage)
			setValue('currentPage', newPage)
		}
	}

	useEffect(() => {
		const el = modalContentRef.current
		if (!isOpen || !el) return
		const updateWidth = () => setContentWidth(el.clientWidth)
		updateWidth()
		const ro = new ResizeObserver(updateWidth)
		ro.observe(el)
		return () => ro.disconnect()
	}, [isOpen, previewStatus])

	useEffect(() => {
		if (canvasRefs.current.length) {
			canvasRefs.current = [...canvasRefs.current]
		}
	}, [scale])

	useEffect(() => {
		if (isOpen && isChatOpen) {
			document.body.classList.add('zoho-widget-hidden')
		} else {
			document.body.classList.remove('zoho-widget-hidden')
		}
		return () => {
			document.body.classList.remove('zoho-widget-hidden')
		}
	}, [isOpen, isChatOpen])

	useEffect(() => {
		const el = modalContentRef.current
		if (!el || !numPages || fullPages.length === 0) return
		const run = () =>
			handleScroll({ currentTarget: el } as React.UIEvent<HTMLDivElement>)
		const id1 = requestAnimationFrame(run)
		const id2 = setTimeout(run, 200)
		return () => {
			cancelAnimationFrame(id1)
			clearTimeout(id2)
		}
	}, [fullPages.length, scale, contentWidth, numPages])

	const scrollToPage = (pageIndex: number) => {
		isScrolling.current = true
		const canvasContainer = canvasRefs.current[pageIndex]
		if (canvasContainer) {
			canvasContainer.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}
		setTimeout(() => {
			isScrolling.current = false
		}, 500)
		setValue('paginationPage', pageIndex + 1)
		setValue('currentPage', pageIndex + 1)
	}

	const renderFilePreviewError = (
		previewStatus: string,
		previewError: string | null,
	) => {
		if (
			!renderError &&
			(['ready', 'loading', 'idle'].includes(previewStatus) ||
				(PREVIEW_LOADING_STATUSES as readonly string[]).includes(previewStatus))
		) {
			return null
		}
		const message = renderError || previewError || errors[previewStatus]?.()
		const showLoading = false
		if (!message) return null
		return (
			<div
				role="alert"
				className="absolute inset-0 flex items-center justify-center p-4 font-medium text-lg"
			>
				{isPasswordProtected ? (
					<PasswordProtectedFile
						key={downloadUrl}
						message={message}
						onUnlock={unlockPdf}
					/>
				) : (
					<div className="flex items-center justify-center gap-2 text-center text-text-main dark:text-text-main-dark">
						{showLoading ? (
							<Loader size={20} />
						) : (
							<ErrorOutline className="fill-negative" />
						)}
						<span>{message}</span>
					</div>
				)}
			</div>
		)
	}

	return (
		<Fragment>
			<AnimatePresence>
				{isOpen && (
					<motion.div
						key="backdrop"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className={cn(
							'fixed inset-0 bg-black/20 z-[102] flex dark:bg-black/50',
							position === 'left' ? 'justify-start' : 'justify-end',
							classNameOverlay,
						)}
						onClick={handleClose}
					>
						<motion.div
							key="modal"
							initial={{ opacity: 0, scale: 0.98 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.98 }}
							transition={{ duration: 0.25, ease: 'easeInOut' }}
							className={cn(
								'h-full flex flex-col bg-white dark:bg-background-dark shadow-lg relative w-full md:w-3/4 lg:w-2/3 xl:w-1/2',
								classNameModal,
							)}
							onClick={(e) => e.stopPropagation()}
						>
							<ChatsMenu
								isOpen={isMenuOpen}
								onClose={() => setIsMenuOpen(false)}
								onSelect={setSelectedChat}
								onSave={() => setSaveModalStatus('create')}
								selectedChat={selectedChat}
								currentFileId={fileId ?? undefined}
							/>
							<Chat
								isOpen={isChatOpen && isMobile}
								onClose={() => {
									setIsMenuOpen(false)
									setIsChatOpen(false)
									setSelectedChat(null)
								}}
								onOpenMenu={() => setIsMenuOpen(true)}
								onSave={() => setSaveModalStatus('close')}
								onSelect={setSelectedChat}
								isMenuOpen={isMenuOpen}
								currentFileId={fileId ?? undefined}
								selectedChat={selectedChat}
							/>
							<div className="flex flex-col flex-1 min-h-0 overflow-hidden relative h-full">
								<FormProvider {...methods}>
									<PreviewHeader
										isChatOpened={isChatOpen}
										isModal={true}
										isApp={false}
										onClose={handleClose}
										onFileInfo={() => setIsFileInfoOpen((prev) => !prev)}
										onOpenChat={() => setIsChatOpen(true)}
										currentDocument={currentDocument}
										versionId={versionId}
										isLoading={isLoadingFile}
										isNotSupported={
											previewStatus === 'file_not_supported_format'
										}
									/>
								</FormProvider>

								{/* Absolute positioning — in webview flex-1 often doesn't give height, then scroll fails */}
								<div
									ref={modalContentRef}
									className="bg-background-light custom-scrollbar absolute left-0 right-0 bottom-0 dark:bg-background-light-dark overflow-auto"
									style={{
										top: '5rem',
										WebkitOverflowScrolling: 'touch',
									}}
									onScroll={handleScroll}
								>
									{showOverlayLoader && !isError && (
										<div className="absolute inset-0 flex items-center justify-center gap-2 z-20 bg-white/70 dark:bg-background-dark/70">
											<Loader size={24} />
											<span className="text-text-main dark:text-text-main-dark">
												{overlayMessage}
											</span>
										</div>
									)}
									{previewStatus === 'ready' &&
										downloadUrl &&
										!renderError &&
										!isError &&
										(shouldUseMobilePreview ? (
											<MobileAppPreview
												downloadUrl={downloadUrl}
												isChatOpen={isChatOpen}
											/>
										) : !isLoading ? (
											<div className="flex flex-col items-center px-0 py-4 gap-4 mx-auto min-w-min shrink-0">
												{Array.from({ length: numPages || 0 }).map(
													(_, index) => {
														const page = fullPages[index]
														const baseCanvas = page || fullPages[0]
														const useScale = Boolean(pdfDocument)
														const baseWidth = baseCanvas?.width || 800
														const baseHeight = baseCanvas?.height || 1000
														const fitWidth =
															contentWidth ||
															(typeof window !== 'undefined'
																? window.innerWidth - 10
																: 800)
														const actualScale =
															useScale && scale !== 'fit'
																? (scale as number)
																: useScale
																	? Math.floor(
																			Math.min(fitWidth / (baseWidth || 1), 2) *
																				100,
																		)
																	: 100
														const scaledWidth = useScale
															? baseWidth * (actualScale / 100)
															: baseWidth
														const scaledHeight = useScale
															? baseHeight * (actualScale / 100)
															: baseHeight
														return (
															<div
																key={`page-${index}-${String(scale)}`}
																className="border border-stroke-light dark:border-stroke-light-dark rounded overflow-hidden bg-white shadow-md transition-all duration-100 flex items-center justify-center shrink-0"
																style={{
																	width: `${scaledWidth}px`,
																	height: `${scaledHeight}px`,
																	minHeight: `${scaledHeight}px`,
																}}
																ref={(el) => {
																	if (el) {
																		canvasRefs.current[index] = el
																	}
																	if (el && page) {
																		page.style.width = `${scaledWidth}px`
																		page.style.height = `${scaledHeight}px`
																		const previousCanvas =
																			el.querySelector('canvas')
																		if (previousCanvas !== page) {
																			if (previousCanvas)
																				el.replaceChild(page, previousCanvas)
																			else el.appendChild(page)
																		}
																	}
																}}
															>
																{!renderedPages.includes(index + 1) &&
																	!showOverlayLoader && (
																		<div className="w-full h-full flex items-center justify-center">
																			<Loader size={30} />
																		</div>
																	)}
															</div>
														)
													},
												)}
											</div>
										) : null)}
									{renderFilePreviewError(previewStatus, previewError)}
								</div>

								{!isLoading &&
									!shouldUseMobilePreview &&
									!isError &&
									previewStatus === 'ready' &&
									!!downloadUrl &&
									!isChatOpen &&
									Number(numPages) > 1 && (
										<div className="animate-appearing group hover:opacity-100 hover:bg-black/90 duration-default absolute w-fit min-w-20 h-10 rounded-lg left-1/2 transform -translate-x-1/2 text-white z-30 bottom-4 flex items-center justify-center gap-2 py-2 px-4 opacity-80 bg-black/60">
											Page
											<Controller
												control={control}
												name="paginationPage"
												render={({ field: { onChange, value } }) => {
													const handlePageInputChange = (
														e: React.ChangeEvent<HTMLInputElement>,
													) => {
														onChange(e.target.value)
													}
													const submitPageChange = () => {
														const pageNum = parseInt(String(value), 10)
														const newPage = Math.min(
															Math.max(pageNum, 1),
															Number(numPages),
														)
														if (!isNaN(newPage) && newPage !== currentPage) {
															scrollToPage(newPage - 1)
														} else {
															onChange(currentPage)
														}
													}
													return (
														<Input
															type="number"
															min={1}
															max={Number(numPages)}
															value={value}
															onChange={handlePageInputChange}
															onBlur={submitPageChange}
															onKeyDown={(e) => {
																if (e.key === 'Enter') {
																	submitPageChange()
																	e.preventDefault()
																}
															}}
															wrapperClassName="w-12 h-7"
															inputClassName="bg-transparent text-white px-1 text-center text-sm"
														/>
													)
												}}
											/>
											of {numPages}
										</div>
									)}
							</div>

							{/* File Info Panel */}
							<AnimatePresence>
								{isFileInfoOpen && (
									<motion.div
										initial={{
											x: position === 'left' ? '-100%' : '100%',
										}}
										animate={{ x: 0 }}
										exit={{
											x: position === 'left' ? '-100%' : '100%',
										}}
										transition={{ duration: 0.2, ease: 'easeInOut' }}
										className={`absolute top-0 ${
											position === 'left'
												? 'left-0 border-r'
												: 'right-0 border-l'
										} w-[300px] md:w-[350px] h-full bg-white dark:bg-background-dark z-30 shadow-lg border-stroke-light dark:border-stroke-light-dark`}
									>
										<FileInfoModal
											manageFileScope={false}
											isOpen={isFileInfoOpen}
											onClose={() => setIsFileInfoOpen(false)}
											fileInfo={
												fileData?.data instanceof File
													? null
													: (fileData?.data ?? null)
											}
											classNameHeader="h-16"
										/>
									</motion.div>
								)}
							</AnimatePresence>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
			<ConfirmationModal
				isOpen={!!saveModalStatus}
				onClose={() => setSaveModalStatus(null)}
				onAction={() => setInputModalStatus(saveModalStatus)}
				onCancel={async () => {
					if (selectedChat?.id && isAuthenticated) {
						await deleteChat(
							{ id: selectedChat?.id },
							{
								onSuccess: () => {
									setSelectedChat(null)
									if (saveModalStatus === 'close') {
										setIsChatOpen(false)
									}
									setSaveModalStatus(null)
								},
								onError: (err: any) => {
									toastCaller({
										type: 'error',
										message:
											'Failed to delete chat. Please try again.' + err.message,
									})
								},
							},
						)
					} else {
						setSaveModalStatus(null)
					}
				}}
				icon={<SmsFailed />}
				title="Save chat"
				description="Current chat has not been saved. Would you like to save it?"
				cancelLabel="No, delete it"
				actionLabel="Yes, save it"
				cancelLoading={isDeleting}
				loading={isChanging}
			/>

			<InputModal
				isOpen={!!inputModalStatus}
				onClose={() => {
					setInputModalStatus(null)
					if (!selectedChat?.isTemporary) {
						setSaveModalStatus(null)
					}
				}}
				onCancel={() => {
					setInputModalStatus(null)
				}}
				title="Save Chat As"
				icon={<Add />}
				loading={isChanging}
				onAction={async (name: string) => {
					if (selectedChat?.id && isAuthenticated && name.trim()) {
						await updateChat(
							{
								data: {
									title: name.trim(),
									isPinned: false,
									isTemporary: false,
								},
								id: selectedChat?.id ?? '',
							},
							{
								onSuccess: (updatedChat) => {
									setSelectedChat(updatedChat.data)
									if (inputModalStatus === 'close') {
										setIsChatOpen(false)
									}
									setSaveModalStatus(null)
									setInputModalStatus(null)
								},
								onError: (err) => {
									toastCaller({
										type: 'error',
										message:
											'Failed to update chat. Please try again.' + err.message,
									})
								},
							},
						)
					}
				}}
			/>
		</Fragment>
	)
}

export default PreviewModal
